// gg-info-tabs — converts the stacked <details> accordion items into a tab UI.
// Tab labels sit in a horizontal strip; one panel shows at a time; active tab underlined.
// The original <details>/<summary> markup is the no-JS fallback: without JS the items
// render as a normal accordion. With JS we build a tab strip and toggle panels.
class GgInfoTabs extends HTMLElement {
  connectedCallback() {
    // Each item is an <accordion-custom> wrapping <details> with a label + content.
    const items = [...this.querySelectorAll('.details')];
    if (items.length === 0) return;

    // Default-active tab = the item marked open-by-default (details[open]);
    // falls back to the first item.
    let defaultIndex = items.findIndex((d) => d.hasAttribute('open'));
    if (defaultIndex < 0) defaultIndex = 0;

    const tabs = items.map((details) => {
      const labelEl = details.querySelector('.gg-accordion__label');
      const contentEl = details.querySelector('.details-content');
      return {
        label: labelEl ? labelEl.textContent.trim() : '',
        content: contentEl,
        details,
      };
    });

    // Build tab strip
    const strip = document.createElement('div');
    strip.className = 'gg-tabs__strip';
    strip.setAttribute('role', 'tablist');

    // Panel area (we move the existing content nodes into it so links/HTML survive)
    const panels = document.createElement('div');
    panels.className = 'gg-tabs__panels';

    tabs.forEach((tab, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gg-tabs__tab';
      btn.textContent = tab.label;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === defaultIndex ? 'true' : 'false');

      const panel = document.createElement('div');
      panel.className = 'gg-tabs__panel';
      panel.setAttribute('role', 'tabpanel');
      if (i !== defaultIndex) panel.hidden = true;
      if (tab.content) panel.appendChild(tab.content); // move the real content in

      btn.addEventListener('click', () => this.#select(i));

      strip.appendChild(btn);
      panels.appendChild(panel);
      tab._btn = btn;
      tab._panel = panel;

      // Hide the original <details> shell now that content has been relocated
      const shell = tab.details.closest('accordion-custom') || tab.details;
      shell.style.display = 'none';
    });

    this._tabs = tabs;
    this.prepend(panels);
    this.prepend(strip);
    this.classList.add('gg-tabs--ready');
  }

  #select(index) {
    this._tabs.forEach((tab, i) => {
      const active = i === index;
      tab._btn.setAttribute('aria-selected', active ? 'true' : 'false');
      tab._panel.hidden = !active;
    });
  }
}

if (!customElements.get('gg-info-tabs')) {
  customElements.define('gg-info-tabs', GgInfoTabs);
}
